import { useEffect } from 'react';

const BASE_URL = 'https://parish.app';

/**
 * Custom hook for SEO - updates document title and meta tags
 */
export function useSEO({ title, description, path = '', image, type = 'website', noIndex = false }) {
  useEffect(() => {
    // Set document title
    const fullTitle = title ? `${title} | Parish` : 'Parish - Catholic Community App';
    document.title = fullTitle;
    
    // Helper to update or create meta tag
    const setMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Helper to update or create link tag
    const setLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    
    const fullUrl = `${BASE_URL}${path}`;
    const ogImage = image || `${BASE_URL}/og-image.png`;
    const metaDescription = description || 'Connect with your Catholic community on Parish.';
    
    // Primary meta tags
    setMeta('title', fullTitle);
    setMeta('description', metaDescription);
    setLink('canonical', fullUrl);
    
    // Robots
    if (noIndex) {
      setMeta('robots', 'noindex, nofollow');
    }
    
    // Open Graph
    setMeta('og:type', type, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', metaDescription, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:site_name', 'Parish', true);
    
    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:url', fullUrl);
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', metaDescription);
    setMeta('twitter:image', ogImage);
    
  }, [title, description, path, image, type, noIndex]);
}

/**
 * Add JSON-LD structured data to the page
 */
export function useStructuredData(data) {
  useEffect(() => {
    if (!data) return;
    
    // Remove existing structured data
    const existing = document.querySelector('script[type="application/ld+json"][data-seo="true"]');
    if (existing) {
      existing.remove();
    }
    
    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    
    return () => {
      script.remove();
    };
  }, [data]);
}

/**
 * Generate Organization structured data
 */
export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Parish",
    "url": BASE_URL,
    "logo": `${BASE_URL}/logo.png`,
    "description": "Catholic community platform connecting parishioners with their faith community"
  };
}

/**
 * Generate LocalBusiness structured data for directory listings
 */
export function getLocalBusinessSchema(business) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": business.name,
    "description": business.description,
    "address": business.address ? {
      "@type": "PostalAddress",
      "streetAddress": business.address
    } : undefined,
    "telephone": business.phone,
    "url": business.website,
    "image": business.image
  };
}

/**
 * Generate Church/Place of Worship structured data
 */
export function getChurchSchema(parish) {
  return {
    "@context": "https://schema.org",
    "@type": "Church",
    "name": parish.name,
    "description": parish.description,
    "address": parish.address ? {
      "@type": "PostalAddress",
      "streetAddress": parish.address,
      "addressLocality": parish.city,
      "addressRegion": parish.state,
      "postalCode": parish.zip
    } : undefined,
    "telephone": parish.phone,
    "url": parish.website,
    "geo": parish.latitude && parish.longitude ? {
      "@type": "GeoCoordinates",
      "latitude": parish.latitude,
      "longitude": parish.longitude
    } : undefined
  };
}
